import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import s from "./MainFilter.module.css";
import {SelectBasic} from "../SelectBasic";
import sButtons from "../../../assets/styles/buttons.module.css";
import {
    mainSelectData,
    MapSettings,
    MapSettingsCreate,
    MapSettingsIndicatorDataRF,
    mapSettingsIndicatorDataRFTitles,
    MapSettingsIndicatorType
} from "../../../services/models/map_settings";
import {useFetchPollTemplates} from "../../../hooks/useQuery";
import {PollTemplate, PollTemplateValue} from "../../../services/models/poll_template";
import {fetchPollTags} from "../../../services/network/poll_tag-api";
import {PollTag} from "../../../services/models/poll_tag";
import translateDate from "../../../Utils/translateDate";
import {useFetchMapSectorsDataRF} from "../../../hooks/useQuery/useFetchMapSectorsDataRF";

interface MainFilterProps {
    mapSettingsData: MapSettings;
    indicatorType: MapSettingsIndicatorType | null;
    updateMapSettings: (updatedValue: MapSettingsCreate) => void;
    indicatorDataRF: MapSettingsIndicatorDataRF;
    selectedYear: number | null;
    onYearChange: (year: number | null) => void;
    isMinzdravUser: boolean;
}

type SelectOption = [string, string]; // [id, title]

// Общая функция для преобразования массива в формат селектора
const createSelectOptions = <T extends { id: string; title?: string; date?: string | null; dateEnd?: string | null }>(
    items: T[] | undefined,
    getValue: (item: T) => string = (item) => item.title || ''
): SelectOption[] => {
    if (!items) return [];
    return items.map(item => [
        item.id,
        getValue(item)
    ]);
};

export const MainFilter: React.FC<MainFilterProps> = memo(({
                                                               mapSettingsData,
                                                               indicatorType,
                                                               updateMapSettings,
                                                               indicatorDataRF,
                                                               selectedYear,
                                                               onYearChange,
                                                               isMinzdravUser
                                                           }) => {

    const {pollTemplatesData} = useFetchPollTemplates();
    const {mapSectorsDataRF} = useFetchMapSectorsDataRF();
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [localIndicatorType, setLocalIndicatorType] = useState<MapSettingsIndicatorType | null>(indicatorType);
    const [localIndicatorDataRF, setLocalIndicatorDataRF] = useState<MapSettingsIndicatorDataRF>(indicatorDataRF);
    const [initialTemplate, setInitialTemplate] = useState<PollTemplate | null>(null);
    const [pollTemplate, setPollTemplate] = useState<PollTemplate | null>(null);
    const [tags, setTags] = useState<PollTag[]>([]);
    const [values, setValues] = useState<PollTemplateValue[]>([]);
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [selectedModule, setSelectedModule] = useState<string | null>(null);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);

    // Эффект для установки initialTemplate при изменении pollTemplatesData или mapSettingsData
    useEffect(() => {
        if (mapSettingsData?.tag?.title && pollTemplatesData) {
            const template = pollTemplatesData.find(
                template => template.title === mapSettingsData.tag.title
            );
            setInitialTemplate(template || null);
            setPollTemplate(template || null);
        }
    }, [pollTemplatesData, mapSettingsData?.tag?.title]);

    // Эффект для инициализации начальных значений из mapSettingsData
    useEffect(() => {
        // Инициализация только при первой загрузке
        if (mapSettingsData && !localIndicatorType) {
            setLocalIndicatorType(mapSettingsData.indicatorType);
            setLocalIndicatorDataRF(mapSettingsData.indicatorDataRF);

            if (mapSettingsData.tag?.id && tags) {
                setSelectedTag(mapSettingsData.tag.id);
            }
            if (mapSettingsData.module?.id) {
                setSelectedModule(mapSettingsData.module.id);
            }
            if (mapSettingsData.value?.id) {
                setSelectedValue(mapSettingsData.value.id);
            }
        }
    }, [mapSettingsData, tags, localIndicatorType]);

    // Эффект для загрузки тегов
    useEffect(() => {
        if (!pollTemplate) return;

        const fetchTags = async () => {
            setIsLoading(true);
            try {
                const tagsData = await fetchPollTags({poll_template_id: pollTemplate.id});
                if (tagsData) {
                    setTags(tagsData);
                } else {
                    setIsError(true);
                }
            } catch (error) {
                console.error(error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTags();
    }, [pollTemplate]);

    // Эффект для обновления values при изменении модуля
    useEffect(() => {
        if (pollTemplate && selectedModule) {
            const module = pollTemplate.modules.find(m => m.id === selectedModule);
            if (module) {
                setValues(module.values);
            }
        }
    }, [pollTemplate, selectedModule]);

    const updateMapSettingHandler = () => {

        if (localIndicatorType) {
            const updatedValue: MapSettingsCreate = {
                tagId: selectedTag || mapSettingsData?.tag?.id || "",
                moduleId: selectedModule || mapSettingsData?.module?.id || "",
                valueId: selectedValue || mapSettingsData?.value?.id || "",
                colors: mapSettingsData?.colors || [],
                indicatorType: localIndicatorType,
                indicatorDataRF: localIndicatorDataRF,
            };
            updateMapSettings(updatedValue)
        }
    }

    // Обработчик для indicatorType с дополнительной логикой
    const handleIndicatorTypeChange = useCallback((value: string | null) => {
        if (value) {
            const newValue = value as MapSettingsIndicatorType;
            setLocalIndicatorType(newValue);

            if (newValue === 'data_user') {
                // Для data_user устанавливаем template и сохраняем текущие значения
                setPollTemplate(initialTemplate);

                if (mapSettingsData) {
                    if (mapSettingsData.tag?.id) {
                        setSelectedTag(mapSettingsData.tag.id);
                    }
                    if (mapSettingsData.module?.id) {
                        setSelectedModule(mapSettingsData.module.id);
                    }
                    if (mapSettingsData.value?.id) {
                        setSelectedValue(mapSettingsData.value.id);
                    }
                }
            }
            // Всегда обновляем indicatorDataRF
            setLocalIndicatorDataRF(indicatorDataRF);
        }
    }, [indicatorDataRF, initialTemplate, mapSettingsData]);

    // Обработчик для pollTemplate
    const handlePollTemplateChange = useCallback((value: string | null) => {
        if (value && pollTemplatesData) {
            const selectedTemplate = pollTemplatesData.find(template => template.id === value);
            setPollTemplate(selectedTemplate || null);
        } else {
            setPollTemplate(initialTemplate);
        }
        // Сбрасываем зависимые значения
        setSelectedTag(null);
        setSelectedModule(null);
        setSelectedValue(null);
        setValues([]);
        setTags([]);
    }, [pollTemplatesData, initialTemplate]);

    // Обработчик для indicatorDataRF
    const handleIndicatorDataRFChange = useCallback((value: string | null) => {
        if (value) {
            const newValue = value as MapSettingsIndicatorDataRF;
            setLocalIndicatorDataRF(newValue);
            // Сбрасываем выбранный год при смене индикатора
            onYearChange(null);
        }
    }, [onYearChange]);

    // Обработчик для выбора года
    const handleYearChange = useCallback((value: string | null) => {
        if (value) {
            onYearChange(Number(value));
        } else {
            onYearChange(null);
        }
    }, [onYearChange]);

    // Обработчик для изменения модуля
    const handleModuleChange = useCallback((value: string | null) => {
        setSelectedModule(value);
        // Сбрасываем зависимые значения
        setSelectedValue(null);
        setValues([]);
    }, []);

    // Обработчик для изменения тега
    const handleTagChange = useCallback((value: string | null) => {
        setSelectedTag(value);
    }, []);

    // Мемоизированные опции для селекторов
    const pollTemplatesDataForFilter = useMemo(() =>
            createSelectOptions(pollTemplatesData),
        [pollTemplatesData]);

    const tagsForSelect = useMemo(() =>
            createSelectOptions<PollTag>(tags, (tag) => {
                if (!tag.date) return '';
                return translateDate(tag.date, tag.dateEnd || undefined) || '';
            }),
        [tags]);

    const modulesForSelect = useMemo(() =>
            createSelectOptions(pollTemplate?.modules),
        [pollTemplate]);

    const valuesForSelect = useMemo(() =>
            createSelectOptions(values),
        [values]);

    // Проверяем, нужен ли селект года (для новых индикаторов)
    const needsYearSelect = useMemo(() => {
        return localIndicatorType === 'data_rf' && (
            localIndicatorDataRF === 'rateHospitalizationChildrenBeds' ||
            localIndicatorDataRF === 'rateHospitalizationAdultsBeds'
        );
    }, [localIndicatorType, localIndicatorDataRF]);

    // Получаем список годов из реальных данных
    const yearsForSelect = useMemo((): SelectOption[] => {
        if (!mapSectorsDataRF || !localIndicatorDataRF) return [];
        
        // Проверяем, нужен ли селект года для текущего индикатора
        const needsYear = localIndicatorDataRF === 'rateHospitalizationChildrenBeds' ||
                         localIndicatorDataRF === 'rateHospitalizationAdultsBeds';
        
        if (!needsYear) return [];
        
        // Собираем все уникальные годы из данных
        const yearsSet = new Set<number>();
        
        mapSectorsDataRF.forEach(sector => {
            const value = sector.data[localIndicatorDataRF];
            if (value && typeof value === 'object') {
                Object.keys(value).forEach(key => {
                    const year = Number(key);
                    if (!isNaN(year)) {
                        yearsSet.add(year);
                    }
                });
            }
        });
        
        // Преобразуем в массив, сортируем по убыванию и форматируем
        const years = Array.from(yearsSet)
            .sort((a, b) => b - a)
            .map(year => [year.toString(), `${year} г.`] as SelectOption);
        
        return years;
    }, [mapSectorsDataRF, localIndicatorDataRF]);

    const isButtonDisabled = !localIndicatorType || (
        localIndicatorType === 'data_user' 
            ? !(pollTemplate && selectedTag && selectedModule && selectedValue)
            : localIndicatorType === 'data_rf' 
                ? !localIndicatorDataRF || (needsYearSelect && !selectedYear)
                : true
    );

    return (
        <div className={s.filterWrapper}>
            <SelectBasic
                selected={localIndicatorType || ''}
                options={Object.entries(mainSelectData)}
                onChange={handleIndicatorTypeChange}
                disabled={isMinzdravUser}
            />

            {localIndicatorType === "data_rf" && (
                <>
                    <SelectBasic
                        selected={localIndicatorDataRF || ''}
                        options={Object.entries(mapSettingsIndicatorDataRFTitles)}
                        onChange={handleIndicatorDataRFChange}
                    />
                    {needsYearSelect && (
                        <SelectBasic
                            selected={selectedYear?.toString() || ''}
                            options={yearsForSelect}
                            onChange={handleYearChange}
                        />
                    )}
                </>
            )}

            {localIndicatorType === "data_user" && (
                <>
                    <SelectBasic
                        selected={pollTemplate?.id || ''}
                        options={pollTemplatesDataForFilter}
                        onChange={handlePollTemplateChange}
                    />
                    <SelectBasic
                        selected={selectedTag || ''}
                        options={tagsForSelect}
                        onChange={handleTagChange}
                    />
                    <SelectBasic
                        selected={selectedModule || ''}
                        options={modulesForSelect}
                        onChange={handleModuleChange}
                    />
                    <SelectBasic
                        selected={selectedValue || ''}
                        options={valuesForSelect}
                        onChange={setSelectedValue}
                    />
                </>
            )}
            <button className={sButtons.mainButton} disabled={isButtonDisabled} onClick={updateMapSettingHandler}>Применить</button>
        </div>
    );
});